
import React from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import Footer from "@/components/Footer";

const WhitePaper = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1A1F2C] to-[#0E1116] text-white">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <Link to="/">
          <Button variant="outline" className="border-orange-500/20 hover:border-orange-500/60 hover:bg-orange-500/10 text-gray-300 mb-8">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </Link>
        
        <div className="whitepaper bg-[#0E1116]/80 border border-white/10 rounded-xl p-6 md:p-10 shadow-xl">
          <h1 className="text-3xl md:text-4xl font-bold mb-4 text-orange-400">PopCoin ($POP) White Paper</h1>
          <hr className="border-white/20 my-6"/>

          <h2 className="text-2xl font-bold mb-4 text-white">Table of Contents</h2>
          <ol className="list-decimal list-inside mb-8 space-y-1 ml-4">
            <li className="text-gray-300 hover:text-orange-400 transition-colors"><a href="#executive-summary">Executive Summary</a></li>
            <li className="text-gray-300 hover:text-orange-400 transition-colors"><a href="#problem-statement">Problem Statement</a></li>
            <li className="text-gray-300 hover:text-orange-400 transition-colors"><a href="#vision-mission">PopCoin Vision &amp; Mission</a></li>
            <li className="text-gray-300 hover:text-orange-400 transition-colors"><a href="#technical-architecture">Technical Architecture</a>
              <ul className="list-disc list-inside ml-6 my-1">
                <li className="text-gray-400 hover:text-orange-400 transition-colors text-sm"><a href="#blockchain-platform">4.1. Blockchain Platform: Solana</a></li>
                <li className="text-gray-400 hover:text-orange-400 transition-colors text-sm"><a href="#contract-mechanics">4.2. Contract Mechanics &amp; Tax Logic</a></li>
                <li className="text-gray-400 hover:text-orange-400 transition-colors text-sm"><a href="#future-chains">4.3. Future Chains: Ethereum V2 &amp; Popchain PoW</a></li>
              </ul>
            </li>
            <li className="text-gray-300 hover:text-orange-400 transition-colors"><a href="#tokenomics">Tokenomics</a>
              <ul className="list-disc list-inside ml-6 my-1">
                <li className="text-gray-400 hover:text-orange-400 transition-colors text-sm"><a href="#supply-distribution">5.1. Supply &amp; Distribution</a></li>
                <li className="text-gray-400 hover:text-orange-400 transition-colors text-sm"><a href="#tax-structure">5.2. Tax Structure</a></li>
                <li className="text-gray-400 hover:text-orange-400 transition-colors text-sm"><a href="#liquidity-growth">5.3. Liquidity &amp; Growth Mechanisms</a></li>
              </ul>
            </li>
            <li className="text-gray-300 hover:text-orange-400 transition-colors">
              <Link to="/ocean-outlaw-fund" className="text-orange-400 hover:text-orange-300">Ocean Outlaw Fund</Link>
            </li>
            <li className="text-gray-300 hover:text-orange-400 transition-colors"><a href="#roadmap">Roadmap</a></li>
            <li className="text-gray-300 hover:text-orange-400 transition-colors"><a href="#governance-community">Governance &amp; Community</a></li>
            <li className="text-gray-300 hover:text-orange-400 transition-colors"><a href="#security-audits">Security &amp; Audits</a></li>
            <li className="text-gray-300 hover:text-orange-400 transition-colors"><a href="#legal-compliance">Legal &amp; Compliance</a></li>
            <li className="text-gray-300 hover:text-orange-400 transition-colors"><a href="#risks-mitigations">Risks &amp; Mitigations</a></li>
            <li className="text-gray-300 hover:text-orange-400 transition-colors"><a href="#conclusion">Conclusion</a></li>
            <li className="text-gray-300 hover:text-orange-400 transition-colors"><a href="#legal-disclaimer">Legal Disclaimer &amp; License</a></li>
          </ol>

          <section id="executive-summary">
            <h3 className="text-xl font-bold mb-4 text-orange-400">1. Executive Summary</h3>
            <p className="mb-4 text-gray-300">
              PopCoin ($POP) is a Solana-based meme coin with muscle: a wickedly clever twist on DeFi that marries high-seas outlaw swagger with real-world ocean conservation. 
              Born from polluted shores and sharpened by rebellion, PopCoin delivers a deflationary-like tokenomics model, gamified liquidity, and an 8% Growth engine—while 2% of every trade funds the Ocean Outlaw Fund to rescue marine wildlife. 
              We're launching <strong className="text-white">5-5-2025</strong>, capped at 210 million tokens, with a bold roadmap that sails into Ethereum V2 and eventually forks into our own Proof-of-Work blockchain, Popchain.
            </p>
            <ul className="list-disc list-inside mb-6 space-y-2 ml-4">
              <li className="text-gray-300"><strong className="text-white">Total Supply:</strong> 210,000,000 $POP</li>
              <li className="text-gray-300"><strong className="text-white">Launch Date:</strong> 5-5-2025</li>
              <li className="text-gray-300"><strong className="text-white">Tax on Trades:</strong> 8% Growth, 2% Ocean Outlaw Fund, 2% Liquidity</li>
              <li className="text-gray-300"><strong className="text-white">Charitable Impact:</strong> Automated Ocean Outlaw Fund without boardroom virtue signaling</li>
              <li className="text-gray-300"><strong className="text-white">Roadmap:</strong> Solana Launch → CEX Listings → Marine Cleanups → DAO Governance → Ethereum V2 → Popchain PoW</li>
            </ul>
          </section>

          <section id="problem-statement">
            <h3 className="text-xl font-bold mb-4 text-orange-400">2. Problem Statement</h3>
            <p className="mb-4 text-gray-300">
              Too many memes are here today and gone tomorrow—trenches hopelessly laden with puppy carcasses and political flop coins. Developers vanish at the first hint of hype's end, leaving us with empty promises and digital drift.
            </p>
            <p className="mb-6 text-gray-300">
              Our oceans suffer the same fate: slicks of oil and heaping piles of plastic choke marine life, a testament to greed and collective stupidity. PopCoin confronts both scourges by channeling meme energy into real-world rescue, turning the tide on fickle trends and polluted seas.
            </p>
          </section>

          <section id="vision-mission">
            <h3 className="text-xl font-bold mb-4 text-orange-400">3. PopCoin Vision &amp; Mission</h3>
            <p className="mb-2 text-gray-300"><strong className="text-white">Vision:</strong> To create a ruthless, self-sustaining ecosystem that creates waves and funds covert marine rescue operations while rewarding ON-CHAIN rebels.</p>
            <p className="mb-6 text-gray-300"><strong className="text-white">Mission:</strong> Merge meme-driven community energy with tangible ocean conservation, ensuring every swap defies apathy and restores our seas.</p>
          </section>

          <section id="technical-architecture">
            <h3 className="text-xl font-bold mb-4 text-orange-400">4. Technical Architecture</h3>
            <h4 id="blockchain-platform" className="text-lg font-bold mb-3 text-white">4.1. Blockchain Platform: Solana</h4>
            <ul className="list-disc list-inside mb-4 space-y-2 ml-4">
              <li className="text-gray-300">High throughput (65k TPS) for micro-tax efficiency</li>
              <li className="text-gray-300">Low fees (&lt; $0.01 per TX) to maximize user gains</li>
            </ul>
            <h4 id="contract-mechanics" className="text-lg font-bold mb-3 text-white">4.2. Contract Mechanics &amp; Tax Logic</h4>
            <ul className="list-disc list-inside mb-4 space-y-2 ml-4">
              <li className="text-gray-300"><strong className="text-white">Smart Contract:</strong> SPL token with integrated tax</li>
              <li className="text-gray-300">On each transfer:
                <ul className="list-disc list-inside ml-6 mt-1 space-y-1">
                  <li className="text-gray-400"><strong className="text-gray-300">8% Growth:</strong> Reinvested into expansion pools</li>
                  <li className="text-gray-400"><strong className="text-gray-300">2% Ocean Outlaw Fund:</strong> Sent to a burn-proof address triggering on-chain charitable payouts</li>
                  <li className="text-gray-400"><strong className="text-gray-300">2% Liquidity:</strong> Auto-added to the Solana DEX pool for price stability</li>
                </ul>
              </li>
            </ul>
            <h4 id="future-chains" className="text-lg font-bold mb-3 text-white">4.3. Future Chains: Ethereum V2 &amp; Popchain PoW</h4>
            <ul className="list-disc list-inside mb-6 space-y-2 ml-4">
              <li className="text-gray-300"><strong className="text-white">Ethereum V2 Migration:</strong> Broaden reach, tap EVM infrastructure</li>
              <li className="text-gray-300"><strong className="text-white">Popchain PoW Fork:</strong> A sovereign, energy-aware blockchain so anyone can run a full node on a consumer PC or Raspberry Pi and mine their heart's desire of POP.</li>
            </ul>
          </section>

          <section id="tokenomics">
            <h3 className="text-xl font-bold mb-4 text-orange-400">5. Tokenomics</h3>
            <h4 id="supply-distribution" className="text-lg font-bold mb-3 text-white">5.1. Supply &amp; Distribution</h4>
            <ul className="list-disc list-inside mb-4 space-y-2 ml-4">
              <li className="text-gray-300"><strong className="text-white">210,000,000 $POP</strong> total supply</li>
              <li className="text-gray-300"><strong className="text-white">20%</strong> for initial liquidity &amp; ecosystem incentives</li>
              <li className="text-gray-300"><strong className="text-white">10%</strong> reserved for growth partnerships</li>
              <li className="text-gray-300"><strong className="text-white">10%</strong> unlocked over 2 years for team &amp; development</li>
            </ul>

            <h4 id="tax-structure" className="text-lg font-bold mb-3 text-white">5.2. Tax Structure</h4>
            <div className="overflow-x-auto mb-4">
              <table className="min-w-full border border-gray-700 rounded-lg">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="py-3 px-4 text-left text-white">Purpose</th>
                    <th className="py-3 px-4 text-left text-white">% per Trade</th>
                    <th className="py-3 px-4 text-left text-white">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-gray-700">
                    <td className="py-3 px-4 text-gray-300">Growth Pool</td>
                    <td className="py-3 px-4 text-gray-300">8%</td>
                    <td className="py-3 px-4 text-gray-300">Fuel for marketing &amp; partnerships</td>
                  </tr>
                  <tr className="border-t border-gray-700 bg-gray-800/30">
                    <td className="py-3 px-4 text-gray-300">Ocean Outlaw Fund</td>
                    <td className="py-3 px-4 text-gray-300">2%</td>
                    <td className="py-3 px-4 text-gray-300">Direct funding for marine rescues</td>
                  </tr>
                  <tr className="border-t border-gray-700">
                    <td className="py-3 px-4 text-gray-300">Liquidity Provision</td>
                    <td className="py-3 px-4 text-gray-300">2%</td>
                    <td className="py-3 px-4 text-gray-300">Strengthens DEX stability</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h4 id="liquidity-growth" className="text-lg font-bold mb-3 text-white">5.3. Liquidity &amp; Growth Mechanisms</h4>
            <ul className="list-disc list-inside mb-6 space-y-2 ml-4">
              <li className="text-gray-300"><strong className="text-white">Auto-Liquidity Addition:</strong> Ensures price floors and thwarts rug risks</li>
              <li className="text-gray-300"><strong className="text-white">Growth Pools:</strong> Fund marketing stunts, partnerships, and platform development</li>
            </ul>
          </section>

          <section id="ocean-outlaw-fund">
            <h3 className="text-xl font-bold mb-4 text-orange-400">6. Ocean Outlaw Fund</h3>
            <p className="mb-6 text-gray-300"><strong className="text-white">No Flipper Left Behind.</strong> Every trade feeds an on-chain wallet dedicated to real-world marine conservation. We publish quarterly impact reports—no sappy selfies, just real rescues. <Link to="/ocean-outlaw-fund" className="text-orange-400 hover:underline">Learn more about the Ocean Outlaw Fund →</Link></p>
          </section>

          <section id="roadmap">
            <h3 className="text-xl font-bold mb-4 text-orange-400">7. Roadmap</h3>
            <ol className="list-decimal list-inside mb-6 space-y-2 ml-4">
              <li className="text-gray-300"><strong className="text-white">Phase 1: Hull Breach</strong> – Launch &amp; liquidity adds (5-5-2025)</li>
              <li className="text-gray-300"><strong className="text-white">Phase 2: Money-Count</strong> – CEX listings &amp; whale alerts</li>
              <li className="text-gray-300"><strong className="text-white">Phase 3: Outlaws Deploy</strong> – First Ocean Outlaw Fund payouts &amp; cleanups</li>
              <li className="text-gray-300"><strong className="text-white">Phase 4: Deep Dive</strong> – DAO governance launch</li>
              <li className="text-gray-300"><strong className="text-white">Phase 5: Ethereum Tide</strong> – V2 launch on Ethereum</li>
              <li className="text-gray-300"><strong className="text-white">Phase 6: Popchain Forge</strong> – Fork to our own Proof-of-Work Blockchain</li>
            </ol>
          </section>

          <section id="governance-community">
            <h3 className="text-xl font-bold mb-4 text-orange-400">8. Governance &amp; Community</h3>
            <ul className="list-disc list-inside mb-6 space-y-2 ml-4">
              <li className="text-gray-300"><strong className="text-white">DAO Structure:</strong> $POP holders vote on fund allocations, roadmap changes, and strategic hires</li>
              <li className="text-gray-300"><strong className="text-white">Community Hubs:</strong> Discord, X, Telegram, with on-chain gold rush events and pirate-style proposals</li>
            </ul>
          </section>

          <section id="security-audits">
            <h3 className="text-xl font-bold mb-4 text-orange-400">9. Security &amp; Audits</h3>
            <ul className="list-disc list-inside mb-6 space-y-2 ml-4">
              <li className="text-gray-300">Audited by top-tier firms (e.g., CertiK, Hacken)</li>
              <li className="text-gray-300">Multi-sig admin keys and time-locked contract functions</li>
            </ul>
          </section>

          <section id="legal-compliance">
            <h3 className="text-xl font-bold mb-4 text-orange-400">10. Legal &amp; Compliance</h3>
            <ul className="list-disc list-inside mb-6 space-y-2 ml-4">
              <li className="text-gray-300"><strong className="text-white">Jurisdiction:</strong> Cayman Islands entity for tax efficiency</li>
              <li className="text-gray-300"><strong className="text-white">KYC/AML:</strong> No immediate requirements; optional for advanced staking tiers</li>
            </ul>
          </section>

          <section id="risks-mitigations">
            <h3 className="text-xl font-bold mb-4 text-orange-400">11. Risks &amp; Mitigations</h3>
            <ul className="list-disc list-inside mb-6 space-y-2 ml-4">
              <li className="text-gray-300"><strong className="text-white">Market Volatility:</strong> Auto-liquidity and growth pools stabilize price</li>
              <li className="text-gray-300"><strong className="text-white">Regulatory Changes:</strong> DAO can adjust on-chain parameters</li>
              <li className="text-gray-300"><strong className="text-white">Smart Contract Bugs:</strong> Formal audits and bug-bounty program</li>
            </ul>
          </section>

          <section id="conclusion">
            <h3 className="text-xl font-bold mb-4 text-orange-400">12. Conclusion</h3>
            <p className="mb-6 text-gray-300">PopCoin flips the script on meme coins: ruthless on price manipulation, relentless on ocean rescue. Join the revolt—trade fierce, fund fearless, and let no flipper go unfound.</p>
          </section>

          <hr className="border-white/20 my-8"/>

          <section id="legal-disclaimer">
            <h3 className="text-xl font-bold mb-4 text-orange-400">13. Legal Disclaimer &amp; License</h3>
            <p className="mb-6 text-gray-300"><strong className="text-white">Legal Disclaimer:</strong> This White Paper is for informational purposes only and does not constitute financial advice. PopCoin and its team make no warranties regarding the accuracy or completeness of this document. Trading cryptocurrencies involves risk, and you may lose all of your investment. Consult a qualified professional before making any financial decisions.</p>

            <pre className="bg-black text-green-500 p-4 rounded-lg mb-8 overflow-x-auto text-xs md:text-sm">
MIT License

Copyright (c) 2025 PopCoin

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
            </pre>
          </section>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default WhitePaper;
